import * as React from 'react';
import { Waypoint } from '../types';

const { useRef, useState } = React;

interface Props {
  waypoints: Waypoint[];
  onChange: (wps: Waypoint[]) => void;
  onAdd: () => void;
  onPickToggle: () => void;
  pickMode: boolean;
  showRadiuses: boolean;
  radiuses: string;
  onRadiusesChange: (v: string) => void;
  cityMode: boolean;
}

// ---------------------------------------------------------------------------
// Coordinate detection helpers
// ---------------------------------------------------------------------------

/** Parse a "lon, lat" or "lat, lon" string (comma or whitespace separated).
 *  Smart detection: if one number is outside ±90 it must be the longitude.
 *  When both are within ±90 (ambiguous), the first number is treated as lon
 *  because that matches ORS / GeoJSON / OpenLayers convention and what the
 *  user is most likely to paste while working in the lon field.
 *  Returns {lon, lat} or null if not a valid pair. */
function parseCoordinatePair(str: string): { lon: number; lat: number } | null {
  const s = str.trim();
  // Match: optional-sign digits optional-decimal  SEPARATOR  optional-sign digits optional-decimal
  const m = s.match(/^(-?\d+(?:\.\d+)?)\s*[,\s]\s*(-?\d+(?:\.\d+)?)$/);
  if (!m) return null;
  const a = parseFloat(m[1]);
  const b = parseFloat(m[2]);
  if (isNaN(a) || isNaN(b)) return null;

  const aValidLat = a >= -90 && a <= 90;
  const aValidLon = a >= -180 && a <= 180;
  const bValidLat = b >= -90 && b <= 90;
  const bValidLon = b >= -180 && b <= 180;

  if (!aValidLon && !aValidLat) return null;
  if (!bValidLon && !bValidLat) return null;

  // If a is clearly a longitude (> ±90) and b is a valid latitude → [lon, lat]
  if (!aValidLat && bValidLat) return { lon: a, lat: b };
  // If b is clearly a longitude (> ±90) and a is a valid latitude → swap to [lon, lat]
  if (!bValidLat && aValidLat) return { lon: b, lat: a };

  // Both fit either role — treat as "lon, lat" (natural paste order when working with ORS/GeoJSON)
  return { lon: a, lat: b };
}

/** Parse a multi-line / semicolon-separated block of coordinate pairs.
 *  Returns an array only when EVERY non-empty line is a valid pair. */
function parseMultipleCoords(str: string): Array<{ lon: number; lat: number }> | null {
  // First try JSON-style array format: [[lon,lat],[lon,lat],...] or [lon,lat],[lon,lat],...
  const jsonResult = parseJsonCoordArray(str);
  if (jsonResult) return jsonResult;

  const lines = str.split(/[\n\r;]+/).map(l => l.trim()).filter(Boolean);
  if (lines.length < 2) return null;
  const pairs = lines.map(parseCoordinatePair);
  if (pairs.some(p => p === null)) return null;
  return pairs as Array<{ lon: number; lat: number }>;
}

/** Parse JSON-style coordinate arrays:
 *  - [[lon,lat],[lon,lat],[lon,lat]]
 *  - [lon,lat],[lon,lat],[lon,lat]
 *  Returns array of {lon, lat} or null if not matching. */
function parseJsonCoordArray(str: string): Array<{ lon: number; lat: number }> | null {
  let s = str.trim();
  // Wrap bare comma-separated pairs: [a,b],[c,d] → [[a,b],[c,d]]
  if (/^\[[\d.\-]/.test(s) && !s.startsWith('[[')) {
    s = '[' + s + ']';
  }
  // Must look like a nested array
  if (!s.startsWith('[[') || !s.endsWith(']]')) return null;
  try {
    const arr = JSON.parse(s);
    if (!Array.isArray(arr) || arr.length < 2) return null;
    const results: Array<{ lon: number; lat: number }> = [];
    for (const item of arr) {
      if (!Array.isArray(item) || item.length !== 2) return null;
      const [lon, lat] = item;
      if (typeof lon !== 'number' || typeof lat !== 'number') return null;
      if (lon < -180 || lon > 180 || lat < -90 || lat > 90) return null;
      results.push({ lon, lat });
    }
    return results;
  } catch {
    return null;
  }
}

// ---------------------------------------------------------------------------

export default function Waypoints({
  waypoints, onChange, onAdd, onPickToggle, pickMode,
  showRadiuses, radiuses, onRadiusesChange,
  cityMode,
}: Props) {
  const dragSrcIdx = useRef<number | null>(null);
  const [dragOver, setDragOver] = useState<{ i: number; after: boolean } | null>(null);

  const update = (i: number, patch: Partial<Waypoint>) => {
    const copy = waypoints.slice();
    copy[i] = { ...copy[i], ...patch };
    onChange(copy);
  };
  const remove = (i: number) => {
    const copy = waypoints.slice();
    copy.splice(i, 1);
    onChange(copy);
  };

  const parseNum = (v: string): number | null => {
    if (v.trim() === '') return null;
    const n = parseFloat(v);
    return isNaN(n) ? null : n;
  };

  // ---- City-mode input handler -------------------------------------------
  /** Handles typing/pasting into the city name field.
   *  Priority: multi-line coordinates → single coordinate → "A->B" split → plain city name. */
  const handleCityInput = (i: number, value: string) => {
    // 1. Multiple coordinate pairs (multiline / semicolon block)
    const multiCoords = parseMultipleCoords(value);
    if (multiCoords) {
      const copy = waypoints.slice();
      const newWps: Waypoint[] = multiCoords.map(({ lon, lat }) => ({
        lon, lat,
        cityName: `${lat}, ${lon}`,   // display text mirrors user input order
      }));
      copy.splice(i, 1, ...newWps);
      onChange(copy);
      return;
    }

    // 2. Single coordinate pair → auto-fill lat/lon, keep text for display
    const single = parseCoordinatePair(value);
    if (single) {
      update(i, { cityName: value, lon: single.lon, lat: single.lat });
      return;
    }

    // 3. "City A -> City B" split (also detect coords inside parts)
    if (value.includes('->')) {
      const parts = value.split('->').map(s => s.trim()).filter(Boolean);
      if (parts.length > 1) {
        const copy = waypoints.slice();
        const newWps: Waypoint[] = parts.map(name => {
          const coord = parseCoordinatePair(name);
          if (coord) return { lon: coord.lon, lat: coord.lat, cityName: name };
          return { lon: null, lat: null, cityName: name };
        });
        copy.splice(i, 1, ...newWps);
        onChange(copy);
        return;
      }
    }

    // 4. Plain city name (clear any previously resolved coords)
    update(i, { cityName: value, lon: null, lat: null });
  };

  // ---- Coordinate-mode lon input handler ---------------------------------
  /** Detects a pasted "lat, lon" pair (or multiline block) in the lon field. */
  const handleLonPaste = (i: number, value: string) => {
    // Multiple coordinate pairs
    const multiCoords = parseMultipleCoords(value);
    if (multiCoords) {
      const copy = waypoints.slice();
      const newWps: Waypoint[] = multiCoords.map(({ lon, lat }) => ({ lon, lat }));
      copy.splice(i, 1, ...newWps);
      onChange(copy);
      return;
    }

    // Single "lat, lon" pair → fill both fields
    const pair = parseCoordinatePair(value);
    if (pair) {
      update(i, { lon: pair.lon, lat: pair.lat });
      return;
    }

    // Normal lon input
    update(i, { lon: parseNum(value) });
  };

  // ---- Drag-reorder -------------------------------------------------------
  const reorder = (from: number, to: number, after: boolean) => {
    if (from === to) return;
    const copy = waypoints.slice();
    const [item] = copy.splice(from, 1);
    let target = to;
    if (from < to) target -= 1;
    if (after) target += 1;
    copy.splice(target, 0, item);
    onChange(copy);
  };

  return (
    <section>
      <div>
        {waypoints.map((wp, i) => {
          const dragging = dragSrcIdx.current === i;
          const over = dragOver?.i === i;
          const cls = `wp${dragging ? ' dragging' : ''}${over ? ' drag-over' : ''}${over && dragOver?.after ? ' drag-over-after' : ''}`;
          const isResolved = cityMode && wp.lon !== null && wp.lat !== null;

          return (
            <div
              key={i}
              className={cls}
              onDragOver={(e) => {
                if (dragSrcIdx.current === null || dragSrcIdx.current === i) return;
                e.preventDefault();
                e.dataTransfer.dropEffect = 'move';
                const rect = e.currentTarget.getBoundingClientRect();
                const after = (e.clientY - rect.top) > rect.height / 2;
                setDragOver({ i, after });
              }}
              onDragLeave={() => setDragOver(null)}
              onDrop={(e) => {
                e.preventDefault();
                const from = dragSrcIdx.current;
                if (from === null || from === i) return;
                const rect = e.currentTarget.getBoundingClientRect();
                const after = (e.clientY - rect.top) > rect.height / 2;
                reorder(from, i, after);
                dragSrcIdx.current = null;
                setDragOver(null);
              }}
            >
              <span
                className="drag-handle"
                title="Drag to reorder"
                draggable
                onDragStart={(e) => {
                  dragSrcIdx.current = i;
                  e.dataTransfer.effectAllowed = 'move';
                  try { e.dataTransfer.setData('text/plain', ''); } catch { /* noop */ }
                }}
                onDragEnd={() => { dragSrcIdx.current = null; setDragOver(null); }}
              >⋮⋮</span>
              <span className="idx">{i + 1}</span>

              {cityMode ? (
                <>
                  <input
                    type="text"
                    placeholder='City, or paste lat,lon (or multi-line)'
                    value={wp.cityName ?? ''}
                    onChange={(e) => handleCityInput(i, e.target.value)}
                    style={{ flex: 1, minWidth: 0 }}
                    title={
                      'Type a city name, or paste a coordinate like "13.627, 50.370" (lon, lat).\n' +
                      'Paste multiple lines to add several stops at once.\n' +
                      'Use "City A -> City B" to split into multiple stops.'
                    }
                  />
                  {isResolved && (
                    <span
                      title={`Resolved: lon ${wp.lon?.toFixed(5)}, lat ${wp.lat?.toFixed(5)}`}
                      style={{ fontSize: '0.85em', color: 'var(--ok, #4caf50)', userSelect: 'none', flexShrink: 0 }}
                    >📍</span>
                  )}
                </>
              ) : (
                <>
                  <input
                    type="text"
                    placeholder="lon  (or paste lon,lat)"
                    value={wp.lon ?? ''}
                    onChange={(e) => handleLonPaste(i, e.target.value)}
                    title='Enter longitude, or paste "lon, lat" to fill both fields at once.'
                  />
                  <input
                    type="text"
                    placeholder="lat  (or paste lon,lat)"
                    value={wp.lat ?? ''}
                    onChange={(e) => {
                      const pair = parseCoordinatePair(e.target.value);
                      if (pair) { update(i, { lon: pair.lon, lat: pair.lat }); return; }
                      update(i, { lat: parseNum(e.target.value) });
                    }}
                    title='Enter latitude, or paste "lon, lat" to fill both fields at once.'
                  />
                </>
              )}

              <button onClick={() => remove(i)} title="Remove">✕</button>
            </div>
          );
        })}
      </div>
      <div className="row" style={{ display: 'flex', gap: 6 }}>
        <button type="button" onClick={onAdd}>+ Add point</button>
        {!cityMode && (
          <button type="button" onClick={onPickToggle}>
            {pickMode ? '🎯 Click map…' : '📍 Pick on map'}
          </button>
        )}
      </div>
      {!cityMode && showRadiuses && (
        <div className="row" title="Per-coordinate snapping radius in meters. -1 to skip. Example: 200,200">
          <label>radiuses <span className="tip" title="Max distance ORS will search for a road to snap each coordinate. Blank = default.">ⓘ</span></label>
          <input
            type="text"
            placeholder="e.g. 200,200 (blank = default)"
            value={radiuses}
            onChange={(e) => onRadiusesChange(e.target.value)}
          />
        </div>
      )}
    </section>
  );
}
